import ApplicationComponent from "legacy/ApplicationComponent"
import FormGroup from "legacy/FormGroup"
import AuthLauncher from "legacy/auth/AuthLauncher"
import NextButton from "legacy/boater-reservation-flow/NextButton"
import CustomFieldsForm from "legacy/boater-reservation-flow/boater-inquiry-flow/CustomFieldsForm"
import InsuranceForm from "legacy/contract-quote/InsuranceForm"
import RegistrationForm from "legacy/contract-quote/RegistrationForm"
import {
  boatDisplayText,
  capitalizeFirstLetter,
  inchesToFeet,
  numbersOnly,
} from "legacy/helpers.es6"
import { validatorFactory } from "legacy/validatorFactory"
import _ from "lodash"
import moment from "moment"
import PropTypes from "prop-types"
import React from "react"

import { snakecaseToCamelKeys } from "src/utils/object_helpers"
import openWorkosAuthPopup from "src/utils/openWorkosAuthPopup"

class InquiryForm extends ApplicationComponent {
  constructor(args) {
    super(args)
    this.state = {
      processing: false,
      selectedBoat: null,
      specialRequest: "",
      lengthOverall: null,
      beam: null,
      height: null,
      draft: null,
      boatName: null,
      boatType: "power",
      transactionType: "",
      startDate: "",
      endDate: "",
      insurance: {
        companyName: null,
        policyNumber: null,
        expirationDate: null,
      },
      registration: {
        number: null,
        expirationDate: null,
      },
      heightError: null,
      draftError: null,
      validationMessage: null,
      loginError: null,
      contact: this.props.contact,
    }
    this.canSubmitDraft = false
    this.canSubmitHeight = false
    this.validator = validatorFactory()

    // Bind methods
    this.selectBoat = this.selectBoat.bind(this)
    this.handleBoatSelectChange = this.handleBoatSelectChange.bind(this)
    this.handleTransactionTypeChange =
      this.handleTransactionTypeChange.bind(this)
    this.handleStartDateChange = this.handleStartDateChange.bind(this)
    this.handleEndDateChange = this.handleEndDateChange.bind(this)
    this.handleHeightEvent = this.handleHeightEvent.bind(this)
    this.handleHeightChange = this.handleHeightChange.bind(this)
    this.handleDraftEvent = this.handleDraftEvent.bind(this)
    this.handleDraftChange = this.handleDraftChange.bind(this)
    this.submit = this.submit.bind(this)
    this.openLoginPopup = this.openLoginPopup.bind(this)
    this.hydrateLoggedInState = this.hydrateLoggedInState.bind(this)
  }

  componentWillUnmount() {
    this.cancelPopup?.()
  }

  openLoginPopup() {
    this.setState({ loginError: null })
    this.cancelPopup = openWorkosAuthPopup({
      returnTo: window.location.pathname + window.location.search,
      onSuccess: this.hydrateLoggedInState,
      onError: (message) => this.setState({ loginError: message }),
    })
  }

  hydrateLoggedInState() {
    Promise.all([
      $.get(process.env.BASE_URL + "/account/me", null, null, "json"),
      $.get(process.env.BASE_URL + "/account/boats", null, null, "json"),
    ])
      .then(([user, boats]) => {
        // /account/boats serves the MobileApi::BoatSerializer shape in
        // snake_case (length_overall, boat_type, …). The form was rendered
        // against BuildInquiryFormProps which camelizes via
        // serialize_for_react, so the boat selector reads lengthOverall etc.
        // Convert to match — otherwise boat dimensions render as NaN.
        this.setState({
          loginError: null,
          contact: {
            ...this.state.contact,
            id: user.id,
            name: user.name,
            email: user.email,
            phone: user.phone,
            boats: snakecaseToCamelKeys(boats || []),
          },
        })
      })
      .catch(() => {
        this.setState({
          loginError:
            "Signed in, but we couldn't load your profile. Please refresh.",
        })
      })
  }

  componentDidMount() {
    this.handleHeightChange(null)
    this.handleDraftChange(null)
    if (this.state.contact.boats && this.state.contact.boats.length > 0) {
      this.selectBoat(this.state.contact.boats[0])
    }
  }

  lowDepthConstraint() {
    return (
      this.props.marina.verticalConstraints.depth &&
      this.props.marina.verticalConstraints.depth <= 15
    )
  }

  handleDraftEvent(event) {
    const draft = numbersOnly(event.target.value)
    this.handleDraftChange(draft)
  }

  handleDraftChange(draft) {
    const normalizedDraft = draft === 0 || !draft ? null : draft
    this.setState({ draft: normalizedDraft })

    if (normalizedDraft && normalizedDraft > 1000) {
      this.setState({
        draftError: "The draft may not be greater than 1000.",
      })
      this.canSubmitDraft = false
    } else if (
      this.lowDepthConstraint() &&
      this.props.marina.verticalConstraints.depth < normalizedDraft
    ) {
      this.setState({
        draftError: `This marina supports a maximum draft of ${this.props.marina.verticalConstraints.depth} feet.`,
      })
      this.canSubmitDraft = false
    } else if (!normalizedDraft && this.lowDepthConstraint()) {
      this.setState({ draftError: "This field is required." })
      this.canSubmitDraft = false
    } else {
      this.setState({ draftError: null })
      this.canSubmitDraft = true
    }
  }

  handleHeightEvent(event) {
    const height = numbersOnly(event.target.value)
    this.handleHeightChange(height)
  }

  handleHeightChange(height) {
    const normalizedHeight = height === 0 || !height ? null : height
    this.setState({ height: normalizedHeight })

    if (normalizedHeight && normalizedHeight > 1000) {
      this.setState({
        heightError: "The height may not be greater than 1000.",
      })
      this.canSubmitHeight = false
    } else if (
      this.props.marina.verticalConstraints.height &&
      this.props.marina.verticalConstraints.height < normalizedHeight
    ) {
      this.setState({
        heightError: `This marina supports a maximum clearance of ${this.props.marina.verticalConstraints.height} feet.`,
      })
      this.canSubmitHeight = false
    } else if (
      !normalizedHeight &&
      this.props.marina.verticalConstraints.height
    ) {
      this.setState({ heightError: "This field is required." })
      this.canSubmitHeight = false
    } else {
      this.setState({ heightError: null })
      this.canSubmitHeight = true
    }
  }

  selectBoat(boat) {
    this.validator.hideMessages()
    if (boat) {
      this.setState({
        selectedBoat: boat,
        insurance: boat.insurance,
        registration: boat.registration,
      })
      this.handleHeightChange(boat.height / 12)
      this.handleDraftChange(boat.draw / 12)
    } else {
      this.setState({
        selectedBoat: null,
        insurance: null,
        registration: null,
      })
      this.handleHeightChange(null)
      this.handleDraftChange(null)
    }
  }

  handleBoatSelectChange(e) {
    const boat = _.find(this.state.contact.boats, {
      id: Number(e.target.value),
    })
    this.selectBoat(boat)
  }

  handleTransactionTypeChange(transactionType) {
    this.setState({ transactionType })
    Amplitude.trackEvent("inquiry_flow:transaction_type_pressed", {
      marina_id: this.props.marina.mcom_id,
      marina_name: this.props.marina.name,
      experience_name: "inquiry_flow",
      transaction_type: transactionType,
    })
  }

  handleStartDateChange(startDate) {
    this.setState({ startDate })
  }

  handleEndDateChange(endDate) {
    this.setState({ endDate })
  }

  contractInfo() {
    if (this.hasContractGroup()) {
      return (
        <div>
          <div className="col-sm-12">
            <h3>{this.props.marina.name}</h3>
          </div>
          <div className="col-sm-6">
            <div className="contract-quote-controller border-t4-green paper-page bs-mt-4">
              <p className="text-bold" style={{ fontSize: "2.3em" }}>
                {this.props.contractGroup.name}
              </p>
              <div className="bs-mt-2">
                <strong>
                  {moment(this.props.contractGroup.startDate).format("LL")} -{" "}
                  {moment(this.props.contractGroup.endDate).format("LL")}
                </strong>
              </div>
              <div className="bs-mt-2">
                <strong>Rate: </strong>
                <span>{this.props.contractGroup.rate.rateText}</span>
              </div>
              <div className="bs-mt-2">
                <strong>Billing: </strong>
                <span>{this.props.billingText}</span>
              </div>
              {this.spaceType()}
            </div>
          </div>
        </div>
      )
    } else {
      return (
        <div>
          <div className="col-sm-12">
            <h1 className="h2">
              Request dockage or storage at {this.props.marina.name}
            </h1>
          </div>
          <div className="col-sm-6">
            <div className="contract-quote-controller border-t4-green paper-page bs-mt-4">
              <p className="text-bold" style={{ fontSize: "1.5em" }}>
                General Inquiry
              </p>
              <div className="bs-mt-2">
                <p>
                  Inquire about long term dockage, storage, and other marina
                  services.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    }
  }

  spaceType() {
    if (this.props.contractGroup && this.props.contractGroup.spaceType) {
      return (
        <div className="bs-mt-2">
          <strong>Space Type: </strong>
          <span>
            {capitalizeFirstLetter(this.props.contractGroup.spaceType)}
          </span>
        </div>
      )
    }
  }

  boatSelector() {
    if (this.state.contact.boats && this.state.contact.boats.length) {
      return (
        <div className="form-group">
          <label>Boat</label>
          <select
            className="form-control"
            value={this.state.selectedBoat && this.state.selectedBoat.id}
            onChange={this.handleBoatSelectChange}
          >
            <option value={null}>(add new boat)</option>
            {_.map(this.state.contact.boats, (boat) => (
              <option value={boat.id} key={boat.id}>
                {boatDisplayText(
                  boat.name,
                  inchesToFeet(boat.lengthOverall),
                  boat.boatType
                )}
              </option>
            ))}
          </select>
        </div>
      )
    }
  }

  boatForm() {
    return (
      <div>
        <h4>Boat</h4>
        <div className="row nested-row">
          <div className="col-sm-4">
            <div className="form-group">
              <label>Boat Name</label>
              <input
                className="form-control"
                name="boatName"
                onChange={this.handleInputChange}
                value={this.state.boatName}
              />
              {this.validator.message(
                "boat_name",
                this.state.boatName,
                "required"
              )}
            </div>
          </div>
          <div className="col-sm-4">
            <div className="form-group">
              <label>Boat Type</label>
              <select
                className="form-control"
                name="boatType"
                value={this.state.boatType}
                onChange={this.handleInputChange}
              >
                <option value="power">Power Boat</option>
                <option value="sail">Sailboat</option>
              </select>
              {this.validator.message(
                "boat_type",
                this.state.boatType,
                "required"
              )}
            </div>
          </div>
          <div className="col-sm-4">
            <div className="form-group">
              <label>Length Overall</label>
              <div className="form-control-with-icon">
                <span className="form-control-icon-right">ft</span>
                <input
                  type="text"
                  className="form-control"
                  name="lengthOverall"
                  value={this.state.lengthOverall}
                  onChange={this.handleInputChange}
                  data-content="Please round up to the nearest foot and include bowsprit, swim platform, davits, etc."
                  data-container="body"
                  data-trigger="focus"
                  data-toggle="popover"
                  data-placement="bottom"
                />
              </div>
              {this.validator.message(
                "length_overall",
                this.state.lengthOverall,
                "required|numeric|min:1,num|max:2000,num"
              )}
            </div>
          </div>
        </div>
        <div className="row nested-row">
          <div className="col-sm-4">
            <div className="form-group">
              <label>Beam</label>
              <div className="form-control-with-icon">
                <span className="form-control-icon-right">ft</span>
                <input
                  className="form-control"
                  name="beam"
                  onChange={this.handleInputChange}
                  value={this.state.beam}
                />
              </div>
              {this.validator.message(
                "beam",
                this.state.beam,
                "required|numeric|min:1,num|max:1000,num"
              )}
            </div>
          </div>
          {this.verticalDimensionsForm("col-sm-4")}
        </div>
      </div>
    )
  }

  heightMissing() {
    return (
      !this.state.selectedBoat ||
      (this.state.selectedBoat &&
        (!this.state.selectedBoat.height ||
          this.state.selectedBoat.height >
            this.props.marina.verticalConstraints.height))
    )
  }

  draftMissing() {
    return (
      !this.state.selectedBoat ||
      (this.state.selectedBoat &&
        (!this.state.selectedBoat.draw ||
          this.state.selectedBoat.draw >
            this.props.marina.verticalConstraints.depth))
    )
  }

  showVerticalDimensionsForm() {
    return (
      (this.draftMissing() || this.heightMissing()) &&
      (this.props.marina.verticalConstraints.height ||
        this.lowDepthConstraint())
    )
  }

  verticalDimensionsForm(columnSize) {
    if (this.showVerticalDimensionsForm()) {
      return (
        <div>
          <div className={columnSize}>
            <div className="form-group">
              <label>Height</label>
              <div className="form-control-with-icon">
                <span className="form-control-icon-right">ft</span>
                <input
                  type="text"
                  className="form-control"
                  value={this.state.height}
                  name="height"
                  onChange={this.handleHeightEvent}
                />
                {this.validator.messageAlways("", this.state.heightError, {
                  className: "bs-mb-3 text-danger text-bold",
                })}
              </div>
            </div>
          </div>
          <div className={columnSize}>
            <div className="form-group">
              <label>Draft</label>
              <div className="form-control-with-icon">
                <span className="form-control-icon-right">ft</span>
                <input
                  type="text"
                  className="form-control"
                  value={this.state.draft}
                  name="draft"
                  onChange={this.handleDraftEvent}
                />
                {this.validator.messageAlways("", this.state.draftError, {
                  className: "bs-mb-3 text-danger text-bold",
                })}
              </div>
            </div>
          </div>
        </div>
      )
    }
  }

  registrationMissing() {
    if (this.state.selectedBoat) {
      return (
        !this.state.selectedBoat.registration ||
        !this.state.selectedBoat.registration.number ||
        !this.state.selectedBoat.registration.expirationDate
      )
    } else {
      return true
    }
  }

  insuranceMissing() {
    if (this.state.selectedBoat) {
      return (
        !this.state.selectedBoat.insurance ||
        !this.state.selectedBoat.insurance.companyName ||
        !this.state.selectedBoat.insurance.policyNumber ||
        !this.state.selectedBoat.insurance.expirationDate
      )
    } else {
      return true
    }
  }

  registrationForm() {
    if (
      this.props.registrationRequirement !== "none" &&
      this.registrationMissing()
    ) {
      return (
        <RegistrationForm
          registration={this.state.registration}
          validator={this.validator}
          setParentState={this.handleInputChange}
        />
      )
    }
  }

  insuranceForm() {
    if (this.props.insuranceRequirement !== "none" && this.insuranceMissing()) {
      return (
        <InsuranceForm
          insurance={this.state.insurance}
          validator={this.validator}
          setParentState={this.handleInputChange}
        />
      )
    }
  }

  customFieldsForm() {
    return (
      <CustomFieldsForm
        context={{ type: "Inquiry" }}
        customFields={this.props.customFields}
        handleCustomFields={(customFields) => {
          this.setState({ customFields })
        }}
        marinaName={this.props.marina.name}
      />
    )
  }

  contactForm() {
    // WORKOS_AUTH_ENABLED off: fall back to the pre-WorkOS AuthLauncher
    // modal that posts to Devise's /users/sign_in. Reloads on success
    // since AuthLauncher's old behavior was location.reload().
    if (!window.workosAuthEnabled) {
      return (
        <div>
          <div className="clearfix">
            <span>Have a Dockwa Account?</span>
            <div className="bs-ml-2 inline-block">
              <AuthLauncher type="login" />
            </div>
          </div>
          {this.contactInformation()}
        </div>
      )
    }

    return (
      <div>
        <div className="clearfix">
          <span>Have a Dockwa Account?</span>
          <button
            type="button"
            className="btn btn-link bs-ml-2 bs-p-0"
            onClick={this.openLoginPopup}
          >
            Log In
          </button>
        </div>
        {this.state.loginError && (
          <p className="text-danger bs-mt-2">{this.state.loginError}</p>
        )}
        {this.contactInformation()}
      </div>
    )
  }

  contactInformation() {
    if (!this.state.contact.id) {
      return (
        <div>
          <h4>Contact</h4>
          {this.nameField()}
          <div className="row nested-row">
            <div className="col-sm-6">
              <FormGroup
                name="contact.email"
                label="Email"
                value={this.state.contact && this.state.contact.email}
                onChange={this.handleInputChange}
                validation={this.validator.message(
                  "email",
                  this.state.contact.email,
                  "required|email"
                )}
              />
            </div>
            <div className="col-sm-6">{this.phoneField()}</div>
          </div>
        </div>
      )
    } else if (!this.state.contact.phone && !this.state.contact.name) {
      return (
        <div>
          <h4>Contact</h4>
          <div className="row nested-row">
            <div className="col-sm-6">{this.nameField()}</div>
            <div className="col-sm-6">{this.phoneField()}</div>
          </div>
        </div>
      )
    } else if (!this.state.contact.phone) {
      return (
        <div>
          <h4>Contact</h4>
          {this.phoneField()}
        </div>
      )
    } else if (!this.state.contact.name) {
      return (
        <div>
          <h4>Contact</h4>
          {this.nameField()}
        </div>
      )
    }
  }

  nameField() {
    return (
      <FormGroup
        name="contact.name"
        label="Full Name"
        value={this.state.contact && this.state.contact.name}
        onChange={this.handleInputChange}
        validation={this.validator.message(
          "full_name",
          this.state.contact.name,
          "required"
        )}
      />
    )
  }

  phoneField() {
    return (
      <FormGroup
        name="contact.phone"
        label="Phone Number"
        value={this.state.contact && this.state.contact.phone}
        onChange={this.handleInputChange}
        validation={this.validator.message(
          "phone",
          this.state.contact.phone,
          "required|phone"
        )}
      />
    )
  }

  loggedInForm() {
    if (this.state.selectedBoat) {
      return (
        <div>
          {this.boatSelector()}
          <div className="row nested-row">
            {this.verticalDimensionsForm("col-sm-6")}
          </div>
        </div>
      )
    } else {
      return (
        <div>
          {this.boatSelector()}
          {this.boatForm()}
        </div>
      )
    }
  }

  form() {
    if (this.state.contact.id) {
      return (
        <div>
          {this.contactInformation()}
          {this.loggedInForm()}
        </div>
      )
    } else {
      return (
        <div>
          {this.contactForm()}
          {this.boatForm()}
        </div>
      )
    }
  }

  hasContractGroup() {
    return this.props.contractGroup && this.props.contractGroup.encodedId
  }

  inquiryTransactionTypeSelector() {
    if (!this.hasContractGroup()) {
      return (
        <FormGroup
          autoFocus
          name="transactionType"
          label="Category"
          inputType="select"
          value={this.state.transactionType}
          onChange={(event) =>
            this.handleTransactionTypeChange(event.target.value)
          }
          validation={this.validator.message(
            "category",
            this.state.transactionType,
            "required"
          )}
        >
          <option value="" disabled="true">
            Select Request Category
          </option>
          {_.map(this.props.transactionTypes, (value, key) => (
            <option key={key} value={key}>
              {value}
            </option>
          ))}
        </FormGroup>
      )
    }
  }

  dateInputs() {
    if (!this.hasContractGroup()) {
      return (
        <div className="row nested-row">
          <div className="col-xs-6">
            <FormGroup
              name="inquiry.startDate"
              label="Start Date"
              value={this.state.startDate}
              startDate={moment()}
              endDate={
                this.state.endDate &&
                moment(this.state.endDate).format("YYYY-MM-DD")
              }
              inputType="datepicker"
              onChange={(startDate) =>
                this.handleStartDateChange(
                  moment(startDate).format("YYYY-MM-DD")
                )
              }
            />
          </div>
          <div className="col-xs-6">
            <FormGroup
              name="inquiry.endDate"
              label="End Date"
              value={this.state.endDate}
              startDate={
                (this.state.startDate &&
                  moment(this.state.startDate).format("YYYY-MM-DD")) ||
                moment()
              }
              inputType="datepicker"
              onChange={(endDate) =>
                this.handleEndDateChange(moment(endDate).format("YYYY-MM-DD"))
              }
            />
          </div>
        </div>
      )
    }
  }

  specialRequestForm() {
    return (
      <FormGroup
        name="specialRequest"
        label="Requests"
        inputType="textarea"
        rows="4"
        placeholder={`Please include any details about your Inquiry such as dates, special requests, location, or anything that may help ${this.props.marina.name} serve you better.`}
        value={this.state.specialRequest}
        onChange={this.handleInputChange}
        validation={this.validator.message(
          "special_request",
          this.state.specialRequest,
          "required"
        )}
      />
    )
  }

  submitButton() {
    return (
      <NextButton
        processing={this.state.processing}
        click={this.submit}
        icon=""
        text="Submit Inquiry"
        processingText=" Submitting Inquiry..."
        btnClass="btn btn-primary w-full"
      />
    )
  }

  mainInquiryForm() {
    return (
      <div>
        {this.form()}
        {this.registrationForm()}
        {this.insuranceForm()}
        {this.inquiryTransactionTypeSelector()}
        {this.dateInputs()}
        {this.specialRequestForm()}
        {this.validator.messageAlways("", this.state.validationMessage, {
          className: "bs-mb-3 text-danger text-bold",
        })}
        {this.submitButton()}
      </div>
    )
  }

  showCustomFieldsForm() {
    return this.props.customFields.length > 0 && !this.state.customFields
  }

  submit() {
    this.validator.showMessages()
    if (
      this.validator.allValid() &&
      this.canSubmitHeight &&
      this.canSubmitDraft
    ) {
      this.setState({ processing: true })
      const formData = {
        special_request: this.state.specialRequest,
      }
      if (this.hasContractGroup()) {
        formData.contract_group_id = this.props.contractGroup.encodedId
      } else {
        formData.transaction_type = this.state.transactionType
        formData.start_date = this.state.startDate
        formData.end_date = this.state.endDate
      }
      if (this.state.selectedBoat) {
        formData.boat_id = this.state.selectedBoat.id
        if (this.heightMissing()) {
          formData.height = this.state.height
        }
        if (this.draftMissing()) {
          formData.draw = this.state.draft
        }
      } else {
        formData.boat_name = this.state.boatName
        formData.boat_type = this.state.boatType
        formData.length_overall = this.state.lengthOverall
        formData.beam = this.state.beam
        formData.draw = this.state.draft
        formData.height = this.state.height
      }
      formData.name = this.state.contact.name
      formData.email = this.state.contact.email
      formData.phone = this.state.contact.phone

      if (this.state.registration) {
        formData["registration_attributes[number]"] =
          this.state.registration.number
        formData["registration_attributes[expiration_date]"] =
          this.state.registration.expirationDate
      }
      if (this.state.insurance) {
        formData["insurance_attributes[company_name]"] =
          this.state.insurance.companyName
        formData["insurance_attributes[policy_number]"] =
          this.state.insurance.policyNumber
        formData["insurance_attributes[expiration_date]"] =
          this.state.insurance.expirationDate
      }
      // Add custom fields data
      if (this.props.customFields) {
        formData.custom_fields = this.state.customFields
      }

      $.post(this.props.postUrl, formData, (data) => {
        window.location.href = data.redirect_to
      }).fail((data) => {
        this.setState({
          validationMessage: JSON.parse(data.responseText).error,
          processing: false,
        })
      })
    } else {
      this.forceUpdate()
    }
  }

  render() {
    this.validator.purgeFields()
    return (
      <div className="container">
        {this.contractInfo()}
        <div className="col-sm-6">
          {this.showCustomFieldsForm() && this.customFieldsForm()}
          {!this.showCustomFieldsForm() && this.mainInquiryForm()}
        </div>
      </div>
    )
  }
}

InquiryForm.propTypes = {
  contact: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    boats: PropTypes.array,
  }).isRequired,
  marina: PropTypes.shape({
    name: PropTypes.string.isRequired,
    mcom_id: PropTypes.string.isRequired,
    verticalConstraints: PropTypes.shape({
      depth: PropTypes.number,
      height: PropTypes.number,
    }).isRequired,
  }).isRequired,
  postUrl: PropTypes.string.isRequired,
  contractGroup: PropTypes.shape({
    encodedId: PropTypes.string,
    name: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    spaceType: PropTypes.string,
    rate: PropTypes.shape({
      rateText: PropTypes.string,
    }),
  }),
  billingText: PropTypes.string,
  transactionTypes: PropTypes.object,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  registrationRequirement: PropTypes.string,
  insuranceRequirement: PropTypes.string,
  customFields: PropTypes.array,
}

InquiryForm.defaultProps = {
  registrationRequirement: "none",
  insuranceRequirement: "none",
  customFields: [],
  contractGroup: null,
  transactionTypes: {},
  startDate: "",
  endDate: "",
}

export default InquiryForm
